import { apiClientBff } from './apiBff';

export interface UpsertCustomerPayload {
  name: string;
  cpfCnpj: string;
  email?: string;
  mobilePhone?: string;
  postalCode?: string;
  address?: string;
  addressNumber?: string;
  addressComplement?: string | null;
  addressDistrict?: string;
  phone?: string | null;
}

export interface CreditCardHolderInfo {
  name: string;
  email: string;
  cpfCnpj: string;
  postalCode: string;
  addressNumber: string;
  addressComplement?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
}

export interface TokenizePayload {
  customer: string;
  creditCard: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
  creditCardHolderInfo: CreditCardHolderInfo;
  remoteIp: string;
}

export interface CreateSubscriptionPayload {
  customer: string;
  billingType: 'CREDIT_CARD' | 'PIX' | 'BOLETO';
  nextDueDate: string; // YYYY-MM-DD
  value: number;
  cycle: 'MONTHLY' | 'WEEKLY' | 'BIWEEKLY' | 'YEARLY';
  description?: string;
  planType?: 'producer' | 'professional';
  creditCardToken?: string;
  creditCard?: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
  creditCardHolderInfo?: CreditCardHolderInfo;
  remoteIp?: string;
  email?: string;
  couponCode?: string;
  autoTrialIntent?: boolean;
  autoTrialAllowCreate?: boolean;
}

class AsaasService {
  async upsertCustomer(payload: UpsertCustomerPayload) {
    return apiClientBff.post('/asaas/customers/upsert', payload);
  }

  async tokenize(payload: TokenizePayload) {
    return apiClientBff.post('/asaas/credit-card/tokenize', payload);
  }

  async createSubscription(payload: CreateSubscriptionPayload, opts?: { token?: string }) {
    const token = String(opts?.token || '').trim();
    if (token) {
      return apiClientBff.post('/asaas/subscriptions', payload, {
        skipAuth: true,
        headers: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` },
      });
    }
    return apiClientBff.post('/asaas/subscriptions', payload);
  }

  async listSubscriptionPayments(subscriptionId: string) {
    return apiClientBff.get(`/asaas/subscriptions/${subscriptionId}/payments`);
  }

  async getSubscription(subscriptionId: string) {
    return apiClientBff.get(`/asaas/subscriptions/${subscriptionId}`);
  }

  async checkAccess(subscriptionId: string) {
    return apiClientBff.get(`/asaas/subscriptions/${subscriptionId}/access`);
  }

  async checkAccessByEmail(email: string, opts?: { token?: string }) {
    const token = String(opts?.token || '').trim();
    const response = await apiClientBff.post(
      '/asaas/access/check',
      { email },
      token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : undefined
    );
    return response;
  }

  async checkAccessByEmailAsaasOnly(email: string) {
    return apiClientBff.post('/asaas/access/check', { email, skipCoupons: true });
  }

  async getCurrentAsaasCustomer(email: string) {
    return apiClientBff.get(`/asaas/customer/current?email=${encodeURIComponent(email)}`);
  }

  async checkCoupon(email: string) {
    return apiClientBff.post('/asaas/coupons/check', { email });
  }

  async validateCouponByCode(payload: { couponCode: string; email: string }, opts?: { token?: string }) {
    // Upstream Coupons collection requires the user's JWT (same backend that issued it).
    // When a registration token is available (onboarding flow), send it explicitly via skipAuth.
    // Otherwise let apiClientBff inject the logged-in user's JWT.
    const token = String(opts?.token || '').trim();
    if (token) {
      return apiClientBff.post('/coupons/validate', payload, {
        skipAuth: true,
        headers: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` },
      });
    }
    return apiClientBff.post('/coupons/validate', payload);
  }

  async redeemCouponByCode(payload: { couponCode: string; email: string }, opts?: { token?: string }) {
    const token = String(opts?.token || '').trim();
    if (token) {
      return apiClientBff.post('/coupons/redeem', payload, {
        skipAuth: true,
        headers: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` },
      });
    }
    return apiClientBff.post('/coupons/redeem', payload);
  }

  async bootstrapOnboardingTrial(payload: { email: string; allowCreate?: boolean }, opts?: { token?: string }) {
    const token = String(opts?.token || '').trim();
    if (token) {
      return apiClientBff.post('/coupons/onboarding-trial/bootstrap', payload, {
        skipAuth: true,
        headers: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` },
      });
    }
    return apiClientBff.post('/coupons/onboarding-trial/bootstrap', payload);
  }

  async deleteSubscription(subscriptionId: string) {
    return apiClientBff.delete(`/asaas/subscriptions/${subscriptionId}`);
  }

  async scheduleCancelAtPeriodEnd(subscriptionId: string) {
    return apiClientBff.post(`/asaas/subscriptions/${subscriptionId}/cancelAtPeriodEnd`);
  }
}

export const asaasService = new AsaasService();
